import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";

function collectFeatures(project) {
  const feats = [];
  for (const a of (project?.areas || [])) {
    const b = a.boundary;
    if (!b) continue;
    if (b.type === "FeatureCollection") feats.push(...(b.features || []));
    else if (b.type === "Feature") feats.push(b);
    else feats.push({ type: "Feature", properties: { area: a.area_id }, geometry: b });
  }
  return feats;
}

function FitBounds({ data }) {
  const map = useMap();
  useMemo(() => {
    try {
      const b = L.geoJSON(data).getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [18, 18] });
    } catch (e) { /* ignore */ }
  }, [data, map]);
  return null;
}

// Keeps Leaflet's internal size in lock-step with its (constrained) container.
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const fix = () => map.invalidateSize({ animate: false });
    const t = setTimeout(fix, 60);
    const ro = new ResizeObserver(fix);
    ro.observe(el);
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [map]);
  return null;
}

// Compact dark Leaflet map of the project's licence-area boundaries for the Command Centre.
export function ProjectMiniMap({ project, className = "" }) {
  const feats = useMemo(() => collectFeatures(project), [project]);
  if (!feats.length) {
    return (
      <div className={`grid place-items-center text-xs text-muted-foreground bg-[#04070B] ${className}`} data-testid="command-map-empty">
        Licence map appears once boundaries are uploaded.
      </div>
    );
  }
  const fc = { type: "FeatureCollection", features: feats };
  return (
    <div className={`relative overflow-hidden isolate ${className}`} data-testid="command-map">
      <MapContainer
        className="absolute inset-0 z-0"
        style={{ height: "100%", width: "100%", background: "#04070B" }}
        zoom={5} center={[0, 20]}
        scrollWheelZoom={false} attributionControl={true} zoomControl={true}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="&copy; Esri · Maxar · Earthstar Geographics"
          maxZoom={19}
        />
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />
        <GeoJSON data={fc} style={{ color: "#C20511", weight: 2.5, fillColor: "#C20511", fillOpacity: 0.14 }} />
        <FitBounds data={fc} />
        <InvalidateOnResize />
      </MapContainer>
    </div>
  );
}
